/**
 * Performance Guide Tab Component
 * Comprehensive documentation of all supplier performance calculations and formulas
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import {
  Clock,
  AlertTriangle,
  CheckCircle,
  Info,
  BookOpen,
  Calculator,
  Package,
  Truck,
  Award,
  BarChart3,
  Calendar,
  ArrowRight,
  Target,
  Scale,
} from 'lucide-react';
import { SupplierPerformanceSettings } from '../../../lib/api';
import { PerformanceFormData } from './types';
import { useQualityMeasureLabel, useQualityMeasureLongLabel } from '../../../lib/qualityLabels';

interface PerformanceGuideTabProps {
  settings: SupplierPerformanceSettings | null;
  formData: PerformanceFormData;
}

export function PerformanceGuideTab({
  settings,
  formData,
}: PerformanceGuideTabProps) {
  const qualityLabel = useQualityMeasureLabel();
  const qualityLongLabel = useQualityMeasureLongLabel();
  const qualityDivisorLabel = qualityLabel === 'PPK' ? '1,000' : '1,000,000';
  const qualityUnitNoun = qualityLabel === 'PPK' ? 'thousand' : 'million';
  return (
    <div className="space-y-6">
      {/* Introduction Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-500" />
            <CardTitle>Performance Metrics Guide</CardTitle>
          </div>
          <CardDescription>
            Comprehensive documentation of all supplier performance calculations, formulas, and business logic used in the Helix platform.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 border rounded-lg text-center">
              <Calculator className="h-8 w-8 mx-auto mb-2 text-blue-500" />
              <div className="font-medium">Key Metrics</div>
              <div className="text-sm text-muted-foreground">OTIF, OTD, {qualityLabel}, Arrears</div>
            </div>
            <div className="p-4 border rounded-lg text-center">
              <Award className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
              <div className="font-medium">Rating System</div>
              <div className="text-sm text-muted-foreground">Weighted scores & grades</div>
            </div>
            <div className="p-4 border rounded-lg text-center">
              <BarChart3 className="h-8 w-8 mx-auto mb-2 text-green-500" />
              <div className="font-medium">Forecasting</div>
              <div className="text-sm text-muted-foreground">Time buckets & projections</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* OTIF Explanation */}
      <OTIFExplanationCard formData={formData} />

      {/* OTD Explanation */}
      <OTDExplanationCard />

      {/* PPM Explanation */}
      <PPMExplanationCard
        formData={formData}
        qualityLabel={qualityLabel}
        qualityLongLabel={qualityLongLabel}
        qualityDivisorLabel={qualityDivisorLabel}
        qualityUnitNoun={qualityUnitNoun}
      />

      {/* Arrears Explanation */}
      <ArrearsExplanationCard formData={formData} />

      {/* Rating Calculation */}
      <RatingCalculationCard settings={settings} formData={formData} qualityLabel={qualityLabel} />

      {/* Forecast Explanation */}
      <ForecastExplanationCard />

      {/* Quick Reference Table */}
      <QuickReferenceCard
        formData={formData}
        qualityLabel={qualityLabel}
        qualityDivisorLabel={qualityDivisorLabel}
      />

      {/* Worked Example */}
      <WorkedExampleCard qualityLabel={qualityLabel} />

      {/* Common Pitfalls */}
      <CommonPitfallsCard />

      {/* Glossary */}
      <GlossaryCard qualityLabel={qualityLabel} qualityLongLabel={qualityLongLabel} />
    </div>
  );
}

// OTIF Explanation Card
function OTIFExplanationCard({ formData }: { formData: PerformanceFormData }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          <CardTitle>OTIF (On-Time In-Full)</CardTitle>
        </div>
        <CardDescription>
          The primary measure of delivery performance combining timeliness and completeness.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Info className="h-4 w-4" />
              What is OTIF?
            </h4>
            <p className="text-sm text-muted-foreground">
              OTIF (On-Time In-Full) is a composite metric that measures whether a delivery meets <strong>both</strong> criteria:
            </p>
            <ul className="text-sm space-y-2 ml-4">
              <li className="flex items-start gap-2">
                <Clock className="h-4 w-4 mt-0.5 text-blue-500 flex-shrink-0" />
                <span><strong>On-Time:</strong> Delivery received within the acceptable tolerance window</span>
              </li>
              <li className="flex items-start gap-2">
                <Package className="h-4 w-4 mt-0.5 text-green-500 flex-shrink-0" />
                <span><strong>In-Full:</strong> Quantity received meets or exceeds quantity ordered</span>
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h4 className="font-medium">OTIF Tolerance Window</h4>
            <div className="p-4 bg-muted rounded-lg space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Days Early Allowed:</span>
                <Badge variant="secondary">{formData.otif_days_early_allowed} days</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Days Late Allowed:</span>
                <Badge variant="secondary">{formData.otif_days_late_allowed} day(s)</Badge>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              This is the <strong>platform default</strong> applied to all suppliers. Individual suppliers can have <strong>custom tolerance windows</strong> configured which override this default. Manage overrides in OTIF Settings → "Manage Supplier OTIF Overrides".
            </p>
          </div>
        </div>

        <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg space-y-3">
          <div className="flex items-center gap-2 font-medium">
            <Calculator className="h-4 w-4" />
            OTIF Formula
          </div>
          <code className="block text-sm p-3 bg-background rounded border font-mono">
            OTIF % = (Count of Deliveries with OTIF=1) / (Total Deliveries {formData.include_arrears_in_otif && '+ Arrears'}) × 100
          </code>
          <div className="text-sm space-y-1">
            <p><strong>OTIF=1</strong> when: Receipt Date is within [{-formData.otif_days_early_allowed} days, +{formData.otif_days_late_allowed} day(s)] of Delivery Date <strong>AND</strong> Qty Received ≥ Qty Ordered</p>
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="font-medium">OTIF Status Classifications</h4>
          <div className="grid gap-2 md:grid-cols-3">
            <div className="flex items-center gap-2 p-2 border rounded">
              <Badge className="bg-green-500">OTIF</Badge>
              <span className="text-sm">On-Time & In-Full (Pass)</span>
            </div>
            <div className="flex items-center gap-2 p-2 border rounded">
              <Badge variant="secondary">Early</Badge>
              <span className="text-sm">Delivered early</span>
            </div>
            <div className="flex items-center gap-2 p-2 border rounded">
              <Badge variant="destructive">Late</Badge>
              <span className="text-sm">Delivered after due date</span>
            </div>
            <div className="flex items-center gap-2 p-2 border rounded">
              <Badge className="bg-orange-500">Short</Badge>
              <span className="text-sm">Less than ordered qty</span>
            </div>
            <div className="flex items-center gap-2 p-2 border rounded">
              <Badge className="bg-red-600">Arrear</Badge>
              <span className="text-sm">Overdue & not received</span>
            </div>
            <div className="flex items-center gap-2 p-2 border rounded">
              <Badge variant="outline">Pending</Badge>
              <span className="text-sm">Not yet due</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// OTD Explanation Card
function OTDExplanationCard() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Truck className="h-5 w-5 text-blue-500" />
          <CardTitle>OTD (On-Time Delivery)</CardTitle>
        </div>
        <CardDescription>
          Measures delivery timeliness without considering quantity completeness.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Info className="h-4 w-4" />
              OTD vs OTIF
            </h4>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>OTD is a simpler metric that only checks <strong>timeliness</strong>:</p>
              <ul className="list-disc ml-4 space-y-1">
                <li>Does NOT check quantity (in-full)</li>
                <li>Does NOT use tolerance windows</li>
                <li>Strictly: Receipt Date ≤ Delivery Date</li>
              </ul>
            </div>
          </div>
          <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg space-y-3">
            <div className="flex items-center gap-2 font-medium">
              <Calculator className="h-4 w-4" />
              OTD Formula
            </div>
            <code className="block text-sm p-3 bg-background rounded border font-mono">
              OTD % = (On-Time GRNs / Total GRNs) × 100
            </code>
            <p className="text-sm">
              <strong>On-Time:</strong> Receipt Date ≤ Requested Delivery Date
            </p>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-2">OTD Status Values</h4>
          <div className="flex gap-4 text-sm">
            <span className="flex items-center gap-1"><Badge className="bg-green-500">Early</Badge> Receipt &lt; Due Date</span>
            <span className="flex items-center gap-1"><Badge className="bg-blue-500">On-Time</Badge> Receipt = Due Date</span>
            <span className="flex items-center gap-1"><Badge variant="destructive">Late</Badge> Receipt &gt; Due Date</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// PPM Explanation Card
function PPMExplanationCard({
  formData,
  qualityLabel,
  qualityLongLabel,
  qualityDivisorLabel,
  qualityUnitNoun,
}: {
  formData: PerformanceFormData;
  qualityLabel: string;
  qualityLongLabel: string;
  qualityDivisorLabel: string;
  qualityUnitNoun: string;
}) {
  // Reference table row for "Parts / scale = X% defect rate". Values scale
  // with the configured divisor (PPM: 500/1,000,000; PPK: 0.5/1,000 etc.).
  const refRows: Array<{ count: number; pct: string }> = qualityLabel === 'PPK'
    ? [
        { count: 0.5, pct: '0.05%' },
        { count: 1, pct: '0.1%' },
        { count: 5, pct: '0.5%' },
        { count: 10, pct: '1%' },
      ]
    : [
        { count: 500, pct: '0.05%' },
        { count: 1_000, pct: '0.1%' },
        { count: 5_000, pct: '0.5%' },
        { count: 10_000, pct: '1%' },
      ];
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          <CardTitle>{qualityLabel} ({qualityLongLabel})</CardTitle>
        </div>
        <CardDescription>
          Quality metric measuring defect rate per {qualityUnitNoun} parts received.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Info className="h-4 w-4" />
              Understanding {qualityLabel}
            </h4>
            <p className="text-sm text-muted-foreground">
              {qualityLabel} standardizes defect rates across suppliers with different volumes. A {qualityLabel} of {refRows[1]!.count.toLocaleString()} means {refRows[1]!.count.toLocaleString()} defective parts per {qualityUnitNoun} received ({refRows[1]!.pct} defect rate).
            </p>
            <div className="text-sm">
              <strong>Lower {qualityLabel} = Better Quality</strong>
            </div>
          </div>
          <div className="p-4 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg space-y-3">
            <div className="flex items-center gap-2 font-medium">
              <Calculator className="h-4 w-4" />
              {qualityLabel} Formula
            </div>
            <code className="block text-sm p-3 bg-background rounded border font-mono">
              {qualityLabel} = (Reject Qty / Total Received Qty) × {qualityDivisorLabel}
            </code>
            <p className="text-sm">
              Reject quantities are sourced from Non-Conformance Reports (NCRs).
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="font-medium">{qualityLabel} Color Thresholds</h4>
          <div className="grid gap-2 md:grid-cols-3">
            <div className="flex items-center gap-2 p-3 border rounded bg-green-50 dark:bg-green-950/30">
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              <span className="text-sm"><strong>Excellent:</strong> &lt; {formData.ppm_excellent_threshold} {qualityLabel}</span>
            </div>
            <div className="flex items-center gap-2 p-3 border rounded bg-yellow-50 dark:bg-yellow-950/30">
              <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
              <span className="text-sm"><strong>Good:</strong> &lt; {formData.ppm_good_threshold} {qualityLabel}</span>
            </div>
            <div className="flex items-center gap-2 p-3 border rounded bg-red-50 dark:bg-red-950/30">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              <span className="text-sm"><strong>Poor:</strong> &gt; {formData.ppm_poor_threshold} {qualityLabel}</span>
            </div>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-2">{qualityLabel} Reference Guide</h4>
          <div className="grid gap-2 text-sm md:grid-cols-2">
            {refRows.map((row) => (
              <div key={row.pct}>{row.count.toLocaleString()} {qualityLabel} = {row.pct} defect rate</div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Arrears Explanation Card
function ArrearsExplanationCard({ formData }: { formData: PerformanceFormData }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-red-500" />
          <CardTitle>Arrears (Overdue Deliveries)</CardTitle>
        </div>
        <CardDescription>
          Purchase order lines that are past their delivery date and not yet received.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Info className="h-4 w-4" />
              What are Arrears?
            </h4>
            <p className="text-sm text-muted-foreground">
              Arrears represent unfulfilled commitments - PO lines where the supplier has not delivered by the agreed date.
            </p>
            <div className="p-4 border rounded-lg bg-red-50 dark:bg-red-950/30 space-y-2">
              <div className="font-medium text-sm">Arrears Criteria:</div>
              <ul className="text-sm space-y-1 ml-4 list-disc">
                <li>Delivery Date &lt; Today</li>
                <li>Status = Pending or Partially Received</li>
                <li>Not fully received</li>
              </ul>
            </div>
          </div>
          <div className="space-y-4">
            <h4 className="font-medium">Impact on OTIF</h4>
            <div className="p-4 bg-muted rounded-lg text-sm space-y-2">
              <div className="flex items-center gap-2">
                <span>Include Arrears in OTIF:</span>
                <Badge variant={formData.include_arrears_in_otif ? 'default' : 'secondary'}>
                  {formData.include_arrears_in_otif ? 'Yes' : 'No'}
                </Badge>
              </div>
              <p className="text-muted-foreground">
                When enabled, arrears are added to the OTIF denominator, penalizing suppliers for overdue items even before they arrive late.
              </p>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <code className="text-sm font-mono">
                Days Overdue = Today - Delivery Date
              </code>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Rating Calculation Card
function RatingCalculationCard({
  settings,
  formData,
  qualityLabel,
}: {
  settings: SupplierPerformanceSettings | null;
  formData: PerformanceFormData;
  qualityLabel: string;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5 text-yellow-500" />
          <CardTitle>Supplier Rating Calculation</CardTitle>
        </div>
        <CardDescription>
          How OTIF and {qualityLabel} combine to create an overall supplier score and grade.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="p-4 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg space-y-4">
          <div className="flex items-center gap-2 font-medium">
            <Calculator className="h-4 w-4" />
            Rating Formula (3-Step Process)
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Badge className="mt-0.5">1</Badge>
              <div className="space-y-1">
                <div className="font-medium text-sm">Convert OTIF % to Score Points</div>
                <code className="text-xs bg-background p-2 rounded border block">
                  OTIF % → OTIF Score (via OTIF Score Mappings table)
                </code>
                <p className="text-xs text-muted-foreground">Example: 92% OTIF → 90 points</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Badge className="mt-0.5">2</Badge>
              <div className="space-y-1">
                <div className="font-medium text-sm">Convert {qualityLabel} to Score Points</div>
                <code className="text-xs bg-background p-2 rounded border block">
                  {qualityLabel} Value → {qualityLabel} Score (via {qualityLabel} Score Mappings table)
                </code>
                <p className="text-xs text-muted-foreground">Example: 800 {qualityLabel} → 90 points</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Badge className="mt-0.5">3</Badge>
              <div className="space-y-1">
                <div className="font-medium text-sm">Calculate Weighted Combined Score</div>
                <code className="text-xs bg-background p-2 rounded border block">
                  Score = (OTIF Score × {formData.otif_weight}%) + ({qualityLabel} Score × {formData.ppm_weight}%)
                </code>
                <p className="text-xs text-muted-foreground">Example: (90 × 0.70) + (90 × 0.30) = 63 + 27 = 90 points</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="p-4 border rounded-lg">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Scale className="h-4 w-4" />
              Current Weights
            </h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">OTIF Weight:</span>
                <Badge>{formData.otif_weight}%</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">{qualityLabel} Weight:</span>
                <Badge>{formData.ppm_weight}%</Badge>
              </div>
              <div className="flex items-center justify-between border-t pt-2">
                <span className="text-sm font-medium">Total:</span>
                <Badge variant="outline">100%</Badge>
              </div>
            </div>
          </div>

          <div className="p-4 border rounded-lg">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Target className="h-4 w-4" />
              Score to Grade
            </h4>
            <div className="space-y-1 text-sm">
              {settings?.rating_thresholds?.sort((a, b) => Number(b.min_score) - Number(a.min_score)).map((threshold) => (
                <div key={threshold.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-5 h-5 rounded text-white text-xs font-bold flex items-center justify-center"
                      style={{ backgroundColor: threshold.color }}
                    >
                      {threshold.grade}
                    </div>
                    <span>{threshold.display_label || threshold.label || threshold.grade}</span>
                  </div>
                  <span className="text-muted-foreground">{threshold.min_score}-{threshold.max_score}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Forecast Explanation Card
function ForecastExplanationCard() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-purple-500" />
          <CardTitle>Forecast & Time Buckets</CardTitle>
        </div>
        <CardDescription>
          How projected quantities are organized into time-based views.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Info className="h-4 w-4" />
              Time Bucket Structure
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 p-2 border rounded bg-red-50 dark:bg-red-950/30">
                <Badge variant="destructive">Arrears</Badge>
                <span>Past due (Delivery Date &lt; Today)</span>
              </div>
              <div className="flex items-center gap-2 p-2 border rounded">
                <Badge variant="secondary">Wk1-Wk12</Badge>
                <span>Weekly buckets for next 12 weeks</span>
              </div>
              <div className="flex items-center gap-2 p-2 border rounded">
                <Badge variant="outline">M1-M24</Badge>
                <span>Monthly buckets (after week 12)</span>
              </div>
              <div className="flex items-center gap-2 p-2 border rounded">
                <Badge>Future</Badge>
                <span>Beyond 24 months</span>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <h4 className="font-medium">Record Types</h4>
            <div className="space-y-2 text-sm">
              <div className="p-3 border rounded bg-blue-50 dark:bg-blue-950/30">
                <div className="font-medium">Type 1: Forecast</div>
                <p className="text-muted-foreground text-xs">Projected quantities beyond the firm boundary</p>
              </div>
              <div className="p-3 border rounded bg-green-50 dark:bg-green-950/30">
                <div className="font-medium">Type 2: Open PO</div>
                <p className="text-muted-foreground text-xs">Confirmed PO lines within firm boundary</p>
              </div>
              <div className="p-3 border rounded bg-purple-50 dark:bg-purple-950/30">
                <div className="font-medium">Type 3: Total</div>
                <p className="text-muted-foreground text-xs">Sum of Forecast + Open PO quantities</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Quick Reference Card
function QuickReferenceCard({
  formData,
  qualityLabel,
  qualityDivisorLabel,
}: {
  formData: PerformanceFormData;
  qualityLabel: string;
  qualityDivisorLabel: string;
}) {
  // Compact scale notation for the formula column: "1M" for PPM, "1K" for PPK.
  // The long form (1,000 / 1,000,000) is available via `qualityDivisorLabel`.
  const scaleSuffix = qualityLabel === 'PPK' ? '1K' : '1M';
  void qualityDivisorLabel;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-indigo-500" />
          <CardTitle>Quick Reference Summary</CardTitle>
        </div>
        <CardDescription>
          All metrics at a glance for quick reference.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3 font-medium">Metric</th>
                <th className="text-left p-3 font-medium">Formula</th>
                <th className="text-left p-3 font-medium">Good Value</th>
                <th className="text-left p-3 font-medium">Key Insight</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="p-3 font-medium">OTIF</td>
                <td className="p-3 font-mono text-xs">(OTIF Pass / Total) × 100</td>
                <td className="p-3"><Badge className="bg-green-500">≥ 95%</Badge></td>
                <td className="p-3 text-muted-foreground">On-time AND complete deliveries</td>
              </tr>
              <tr className="border-b">
                <td className="p-3 font-medium">OTD</td>
                <td className="p-3 font-mono text-xs">(On-Time GRNs / Total) × 100</td>
                <td className="p-3"><Badge className="bg-green-500">≥ 95%</Badge></td>
                <td className="p-3 text-muted-foreground">Timeliness only (no qty check)</td>
              </tr>
              <tr className="border-b">
                <td className="p-3 font-medium">{qualityLabel}</td>
                <td className="p-3 font-mono text-xs">(Rejects / Received) × {scaleSuffix}</td>
                <td className="p-3"><Badge className="bg-green-500">&lt; 500</Badge></td>
                <td className="p-3 text-muted-foreground">Lower is better (quality)</td>
              </tr>
              <tr className="border-b">
                <td className="p-3 font-medium">Arrears</td>
                <td className="p-3 font-mono text-xs">Count where Due &lt; Today</td>
                <td className="p-3"><Badge className="bg-green-500">0</Badge></td>
                <td className="p-3 text-muted-foreground">Overdue unreceived items</td>
              </tr>
              <tr>
                <td className="p-3 font-medium">Rating</td>
                <td className="p-3 font-mono text-xs">(OTIF×{formData.otif_weight}% + {qualityLabel}×{formData.ppm_weight}%)</td>
                <td className="p-3"><Badge className="bg-green-500">≥ 90</Badge></td>
                <td className="p-3 text-muted-foreground">Weighted combined score</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// Worked Example Card
function WorkedExampleCard({ qualityLabel }: { qualityLabel: string }) {
  const scaleSuffix = qualityLabel === 'PPK' ? '1K' : '1M';
  const exampleValue = qualityLabel === 'PPK' ? '0.5' : '500';
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-violet-500" />
          <CardTitle>Worked Example</CardTitle>
        </div>
        <CardDescription>
          Step-by-step calculation example for a typical month.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-4 bg-background border rounded-lg">
          <h4 className="font-medium mb-3">Scenario: Supplier Performance for January</h4>
          <div className="grid gap-4 md:grid-cols-2 text-sm">
            <div className="space-y-2">
              <div className="font-medium">Delivery Data:</div>
              <ul className="space-y-1 ml-4">
                <li>100 total deliveries due</li>
                <li className="text-green-600 dark:text-green-400">85 on-time and in-full (OTIF pass)</li>
                <li className="text-red-600 dark:text-red-400">5 delivered late</li>
                <li className="text-orange-600 dark:text-orange-400">3 delivered short (partial qty)</li>
                <li className="text-blue-600 dark:text-blue-400">5 delivered too early (outside window)</li>
                <li className="text-red-600 dark:text-red-400">2 arrears (not received, past due)</li>
              </ul>
            </div>
            <div className="space-y-2">
              <div className="font-medium">Quality Data:</div>
              <ul className="space-y-1 ml-4">
                <li>98,000 parts received total</li>
                <li className="text-red-600 dark:text-red-400">49 parts rejected (defects)</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="p-4 border rounded-lg space-y-2">
            <div className="font-medium text-sm">OTIF Calculation</div>
            <code className="text-xs bg-muted p-2 rounded block">
              OTIF = 85 / (100 + 2) × 100
            </code>
            <div className="text-2xl font-bold text-green-600">83.3%</div>
            <p className="text-xs text-muted-foreground">Arrears added to denominator</p>
          </div>
          <div className="p-4 border rounded-lg space-y-2">
            <div className="font-medium text-sm">OTD Calculation</div>
            <code className="text-xs bg-muted p-2 rounded block">
              OTD = 93 / 100 × 100
            </code>
            <div className="text-2xl font-bold text-green-600">93.0%</div>
            <p className="text-xs text-muted-foreground">Early+On-time+Short (received)</p>
          </div>
          <div className="p-4 border rounded-lg space-y-2">
            <div className="font-medium text-sm">{qualityLabel} Calculation</div>
            <code className="text-xs bg-muted p-2 rounded block">
              {qualityLabel} = (49 / 98,000) × {scaleSuffix}
            </code>
            <div className="text-2xl font-bold text-green-600">{exampleValue} {qualityLabel}</div>
            <p className="text-xs text-muted-foreground">0.05% defect rate</p>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <div className="font-medium text-sm mb-2">Final Supplier Rating</div>
          <div className="flex items-center gap-4 text-sm">
            <span>OTIF Score: 80 pts (mapped from 83.3%)</span>
            <ArrowRight className="h-4 w-4" />
            <span>{qualityLabel} Score: 90 pts (mapped from {exampleValue} {qualityLabel})</span>
            <ArrowRight className="h-4 w-4" />
            <code className="bg-background p-1 rounded">(80 × 0.70) + (90 × 0.30) = 83 pts</code>
            <ArrowRight className="h-4 w-4" />
            <Badge className="bg-blue-500">Grade B</Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Common Pitfalls Card
function CommonPitfallsCard() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <CardTitle>Common Pitfalls & Misconceptions</CardTitle>
        </div>
        <CardDescription>
          Important clarifications for accurate metric interpretation.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 border rounded-lg">
            <Badge variant="outline" className="mt-0.5">Myth</Badge>
            <div className="flex-1">
              <div className="font-medium text-sm">"Early delivery is always good"</div>
              <p className="text-sm text-muted-foreground">
                <strong>Reality:</strong> Early deliveries can cause inventory carrying costs, storage issues, and cash flow problems.
                Deliveries outside the early tolerance window may count against OTIF.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 border rounded-lg">
            <Badge variant="outline" className="mt-0.5">Myth</Badge>
            <div className="flex-1">
              <div className="font-medium text-sm">"OTD is always ≥ OTIF"</div>
              <p className="text-sm text-muted-foreground">
                <strong>Reality:</strong> TRUE - OTD doesn't check quantity, so it will always be equal to or higher than OTIF.
                A delivery that's on-time but short will pass OTD but fail OTIF.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 border rounded-lg">
            <Badge variant="outline" className="mt-0.5">Myth</Badge>
            <div className="flex-1">
              <div className="font-medium text-sm">"Small late deliveries don't matter much"</div>
              <p className="text-sm text-muted-foreground">
                <strong>Reality:</strong> Each delivery counts equally in OTIF calculation. A delivery 1 day late counts the same as one 30 days late.
                This is intentional to encourage consistent on-time performance.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 border rounded-lg">
            <Badge variant="outline" className="mt-0.5">Myth</Badge>
            <div className="flex-1">
              <div className="font-medium text-sm">"Low volume suppliers can be ignored"</div>
              <p className="text-sm text-muted-foreground">
                <strong>Reality:</strong> We recommend minimum 30 deliveries for statistically meaningful OTIF %.
                For low-volume suppliers, consider using rolling 90-day or YTD periods.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Glossary Card
function GlossaryCard({ qualityLabel, qualityLongLabel }: { qualityLabel: string; qualityLongLabel: string }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-slate-500" />
          <CardTitle>Glossary of Terms</CardTitle>
        </div>
        <CardDescription>
          Key terminology used throughout the platform.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2 md:grid-cols-2 text-sm">
          <div className="p-2 border-b"><strong>GRN</strong> - Goods Receipt Note (delivery acknowledgment)</div>
          <div className="p-2 border-b"><strong>PO</strong> - Purchase Order</div>
          <div className="p-2 border-b"><strong>NCR</strong> - Non-Conformance Report (quality issue)</div>
          <div className="p-2 border-b"><strong>OTIF</strong> - On-Time In-Full</div>
          <div className="p-2 border-b"><strong>OTD</strong> - On-Time Delivery</div>
          <div className="p-2 border-b"><strong>{qualityLabel}</strong> - {qualityLongLabel} (defect rate)</div>
          <div className="p-2 border-b"><strong>Requested Delivery Date</strong> - Customer's required date</div>
          <div className="p-2 border-b"><strong>Promised Delivery Date</strong> - Supplier's committed date</div>
          <div className="p-2 border-b"><strong>Actual Receipt Date</strong> - Date goods physically received</div>
          <div className="p-2 border-b"><strong>Tolerance Window</strong> - Acceptable delivery timeframe</div>
          <div className="p-2 border-b"><strong>Arrears</strong> - Overdue unreceived PO lines</div>
          <div className="p-2 border-b"><strong>YTD</strong> - Year-To-Date</div>
          <div className="p-2 border-b"><strong>JIT</strong> - Just-In-Time manufacturing</div>
          <div className="p-2 border-b"><strong>Six Sigma</strong> - Quality standard (3.4 defects per million)</div>
        </div>
      </CardContent>
    </Card>
  );
}
